import { Box, Link, Typography } from '@evaneos/b2b-ui';
import LuggageIcon from '@evaneos/b2b-ui/icons/LuggageOutlined';
import { IntlShape, useIntl } from 'react-intl';

import { TravellerTrip } from '../../../resources/dossier/types';
import { DefinitionList } from '../../../design-system/DefinitionList/DefinitionList';
import { useLocale } from '../../../shared/providers/LocalizationProvider';
import { AvailableLocale } from '../../../shared/intl/types';
import { formatBudgetPerDayPerPax } from '../../../resources/dossier/operations';
import { useDossierLayoutInfo } from '../DossierLayoutInfoContext';

type PreviousTripInfoProps = {
    trips: TravellerTrip[] | undefined;
    onOpenTestimonialModal: (trip: TravellerTrip) => void;
};

function formatPreviousTripDisplay(
    trip: TravellerTrip,
    formatMessage: IntlShape['formatMessage'],
    locale: AvailableLocale,
): string {
    const parts = [
        `${trip.destination} (${trip.year} - ${formatMessage({ id: 'project.duration' }, { days: trip.duration })})`,
    ];

    if (trip.budgetPerDayPerPax && trip.budgetCurrency) {
        const budget = formatBudgetPerDayPerPax(
            trip.budgetPerDayPerPax,
            trip.budgetCurrency,
            formatMessage,
            locale,
        );
        if (budget) {
            parts.push(budget);
        }
    }

    return parts.join(' - ');
}

export function PreviousTripInfo({ trips, onOpenTestimonialModal }: PreviousTripInfoProps) {
    const { formatMessage } = useIntl();
    const { locale } = useLocale();
    const { isFirstColumnNarrow } = useDossierLayoutInfo();

    if (!trips || trips.length === 0) {
        return null;
    }

    return (
        <DefinitionList.Item>
            <DefinitionList.Label
                id="previous-trip"
                icon={<LuggageIcon fontSize="small" aria-hidden="true" />}
                iconOnly={isFirstColumnNarrow}
            >
                {formatMessage(
                    { id: 'dossier.information.traveller.previous_trips' },
                    {
                        count: trips.length,
                    },
                )}
            </DefinitionList.Label>
            <DefinitionList.StaticValue>
                <Box
                    component="ul"
                    sx={{
                        maxHeight: '140px',
                        overflowY: 'auto',
                        padding: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                    }}
                >
                    {trips.map((trip) => (
                        <Typography key={trip.id} component="li" variant="body2" sx={{ mb: 1 }}>
                            {formatPreviousTripDisplay(trip, formatMessage, locale)}
                            {trip.hasTestimonial && (
                                <>
                                    {' - '}
                                    <Link
                                        component="button"
                                        sx={{
                                            fontFamily: 'inherit',
                                        }}
                                        onClick={() => onOpenTestimonialModal(trip)}
                                    >
                                        {formatMessage({
                                            id: 'dossier.information.traveller.trips.post_trip_review.link',
                                        })}
                                    </Link>
                                </>
                            )}
                        </Typography>
                    ))}
                </Box>
            </DefinitionList.StaticValue>
        </DefinitionList.Item>
    );
}
